import { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import CheckoutModal from '@/components/CheckoutModal';

const tiers = [
  {
    name: 'Starter',
    subtitle: 'Online Visibility',
    usd: '$350',
    eur: '\u20AC320',
    gbp: '\u00A3280',
    monthlyAmount: 350,
    setupFee: true,
    setupFeeAmount: 150,
    commitmentMonths: 3,
    trial: '1 Month Free Trial',
    commitment: '3-month commitment',
    features: [
      'Google Business Profile Setup & Optimization',
      'Social Media Setup (Facebook & Instagram)',
      '10-15 Posts/Month (Images, Carousels & Reels)',
      'Local Directory Listings (20+ Platforms)',
      'Basic SEO Setup & Optimization',
      'Monthly Performance Report',
    ],
    outcome: 'Get discovered online and start attracting new patients',
    highlighted: false,
  },
  {
    name: 'Growth',
    subtitle: 'Patient Engagement & Website',
    usd: '$550',
    eur: '\u20AC500',
    gbp: '\u00A3440',
    monthlyAmount: 550,
    setupFee: true,
    setupFeeAmount: 200,
    commitmentMonths: 3,
    trial: null,
    commitment: '3-month commitment',
    features: [
      'Everything in Starter',
      'AI-Powered Website with Booking & Payment',
      'AI Assistant for Enquiries & Appointments',
      'Local + National SEO',
      '15-20 Posts/Month + Story Content',
      'Google Business Profile Management',
      'Dedicated Account Manager',
    ],
    outcome: 'Build a strong digital presence that converts visitors into patients',
    highlighted: true,
  },
  {
    name: 'Premium',
    subtitle: 'Full Clinic Growth',
    usd: '$750',
    eur: '\u20AC680',
    gbp: '\u00A3600',
    monthlyAmount: 750,
    setupFee: true,
    setupFeeAmount: 250,
    commitmentMonths: 6,
    trial: null,
    commitment: '6-month commitment',
    features: [
      'Everything in Growth',
      'Custom Website + Blogs Posting',
      '20 Posts + 4 Reels/Month + Video Content',
      'Paid Ads Management (Google, Facebook & Instagram)',
      'Advanced AI Assistant with Full Automation',
      'Advanced SEO (Local + National + AI Search)',
      'Quarterly Growth Strategy Consultation',
      'Dedicated Account Manager',
    ],
    outcome: 'A complete patient growth system for clinics ready to dominate their market',
    highlighted: false,
  },
];

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  return (
    <section id="pricing" className="bg-[#F9F7F2] py-20 lg:py-28" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8C6330] mb-4">Our Packages</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#11181C]">
            Monthly Packages — Results-Driven & Value for Money
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`rounded-2xl p-8 transition-all duration-300 ${
                tier.highlighted
                  ? 'pricing-glow bg-white relative'
                  : 'bg-white border border-[#D6CEBF] shadow-sm hover:shadow-lg'
              }`}
              data-testid={`pricing-${tier.name.toLowerCase()}`}
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1B3F33] text-white text-xs font-bold tracking-wider uppercase px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              {tier.trial && (
                <span className="inline-block bg-[#D4E4DA] text-[#1B3F33] text-xs font-bold px-3 py-1 rounded-full mb-4">
                  {tier.trial}
                </span>
              )}
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8C6330] mb-1">{tier.subtitle}</p>
              <h3 className="font-heading text-2xl font-semibold text-[#11181C] mb-2">{tier.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-4xl font-semibold text-[#1B3F33]">{tier.usd}</span>
                <span className="text-sm text-[#4A5568]">/mo</span>
              </div>
              {tier.setupFee && (
                <p className="text-xs text-[#1B3F33] font-semibold mb-1">+ ${tier.setupFeeAmount} one-time setup fee</p>
              )}
              {tier.commitment && (
                <p className="text-xs text-[#4A5568] italic mb-6">({tier.commitment})</p>
              )}
              {!tier.commitment && <div className="mb-6" />}

              <ul className="space-y-3 mb-8">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#4A5568]">
                    <Check size={16} className="text-[#265E4A] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="bg-[#EDE7D9] rounded-xl p-4 mb-6">
                <p className="text-xs font-bold text-[#1B3F33] uppercase tracking-wider mb-1">Outcome</p>
                <p className="text-sm text-[#4A5568]">{tier.outcome}</p>
              </div>

              <button
                onClick={() => setSelectedPlan(tier)}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 group ${
                  tier.highlighted
                    ? 'bg-[#1B3F33] text-white hover:bg-[#265E4A]'
                    : 'border border-[#1B3F33]/20 text-[#1B3F33] hover:border-[#1B3F33] hover:bg-[#1B3F33] hover:text-white'
                }`}
                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
              >
                Buy Now
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {selectedPlan && (
        <CheckoutModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
      )}
    </section>
  );
};

export default Pricing;
